import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '../elements/Icon';
import { ICON_SEND_V2 } from '../refs/Icons';
import { RecordFileBadge } from './RecordFileBadge';
import { RecordClass } from '../utils/recordFileIcon';

// Literal hex fallbacks — actual values come from the theme when available
const HEX_WHITE           = '#ffffff';
const HEX_LOCHMARA        = '#008dd0';
const HEX_LOCHMARA_SHADOW = '#0072a9';
const HEX_GRAY_40         = '#e5e5e5';
const HEX_GRAY_60         = '#b3b3b3';
const HEX_GRAY150         = '#5a5a5a';
const HEX_MINE_SHAFT      = '#444444';

type AnyProps = { theme?: { colors?: Record<string, string> } };
const t = (token: string, fallback: string) => (props: AnyProps): string =>
  props.theme?.colors?.[token] ?? fallback;

// ─── Types ────────────────────────────────────────────────────────────────────

export type AiChatInputState = 'default' | 'hover' | 'active' | 'filled';

export type AiChatInputProps = {
  /** Name of the record file currently open (shown in the file badge) */
  recordFileName?: string;
  /** Record class (Pathology, Radiology, Clinical, Other) for icon resolution */
  recordClass?: RecordClass;
  /** Explicit file extension (e.g., "pdf", "dcm") for icon resolution */
  fileExtension?: string;
  /** Controlled value of the text input */
  value?: string;
  /** Placeholder text shown when the input is empty */
  placeholder?: string;
  /** Called when the user submits (clicks Send or presses Enter) */
  onSubmit?: (value: string) => void;
  /** Called whenever the input value changes */
  onChange?: (value: string) => void;
  /** Disable the entire component */
  disabled?: boolean;
  className?: string;
};

// ─── Styled components ────────────────────────────────────────────────────────

const Wrapper = styled.div<{ $state: AiChatInputState; $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

// FileBadge is now a separate componentized RecordFileBadge

const InputBox = styled.div<{ $state: AiChatInputState }>`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 16px;
  background-color: ${HEX_WHITE};
  border-radius: 5px;
  box-sizing: border-box;
  position: relative;
  min-height: 96px;
  gap: 8px;

  border: 1px solid
    ${(props: AnyProps & { $state: AiChatInputState }) => {
      switch (props.$state) {
        case 'hover':
        case 'active':
        case 'filled':
          return t('aiPanelMainColor', HEX_LOCHMARA)(props);
        default:
          return HEX_GRAY_40;
      }
    }};

  ${({ $state }) =>
    $state === 'active' &&
    css`
      outline: 2px solid ${HEX_MINE_SHAFT};
      outline-offset: 1px;
    `}
`;

const TEXTAREA_MAX_HEIGHT = 160;

const StyledTextarea = styled.textarea`
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  font-family: 'Open Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  color: ${HEX_MINE_SHAFT};
  min-height: 64px;
  max-height: ${TEXTAREA_MAX_HEIGHT}px;
  overflow-y: auto;

  &::placeholder {
    color: ${HEX_GRAY_60};
  }
`;

const SendButton = styled.button<{ $hasValue: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 5px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  background-color: ${HEX_WHITE};
  position: relative;
  box-sizing: border-box;

  color: ${(props: AnyProps & { $hasValue: boolean }) => (props.$hasValue ? t('aiPanelShadow', HEX_LOCHMARA_SHADOW)(props) : HEX_GRAY_60)};

  &:hover {
    background-color: ${t('aiPanelMainColor', HEX_LOCHMARA)};
    color: ${HEX_WHITE};

    .send-tooltip {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &:active {
    background-color: ${t('aiPanelShadow', HEX_LOCHMARA_SHADOW)};
    color: ${HEX_WHITE};
  }
`;

const SendTooltip = styled.span`
  position: absolute;
  bottom: calc(100% + 4px);
  right: 0;
  background-color: ${HEX_GRAY150};
  color: ${HEX_WHITE};
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 8px 4px;
  border-radius: 3px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
`;

const MAX_QUESTION_LENGTH = 20000;

const CharCounter = styled.span<{ $over: boolean }>`
  font-family: 'Open Sans', sans-serif;
  font-size: 10px;
  color: ${({ $over }) => ($over ? '#cc3333' : HEX_GRAY_60)};
  text-align: right;
  padding-right: 4px;
  flex-shrink: 0;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const AiChatInput: React.FC<AiChatInputProps> = ({
  recordFileName,
  recordClass,
  fileExtension,
  value: controlledValue,
  placeholder = 'Type here. You can ask anything about the record.',
  onSubmit,
  onChange,
  disabled = false,
  className,
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState('');
  const value = isControlled ? controlledValue : internalValue;

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const hasValue = value.trim().length > 0;
  const isOverLimit = value.length >= MAX_QUESTION_LENGTH;
  const showCounter = value.length > MAX_QUESTION_LENGTH * 0.9;

  const resizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
  };

  React.useEffect(() => {
    resizeTextarea();
  }, [value]);

  let state: AiChatInputState = 'default';
  if (isFocused) {
    state = hasValue ? 'filled' : 'active';
  } else if (isHovered) {
    state = 'hover';
  } else if (hasValue) {
    state = 'filled';
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
  };

  const handleSubmit = () => {
    if (!hasValue) return;
    onSubmit?.(value);
    if (!isControlled) setInternalValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Wrapper $state={state} $disabled={disabled} className={className}>
      {recordFileName && <RecordFileBadge recordFileName={recordFileName} recordClass={recordClass} fileExtension={fileExtension} />}
      <InputBox
        $state={state}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => textareaRef.current?.focus()}
      >
        <StyledTextarea
          ref={textareaRef}
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          rows={3}
          disabled={disabled}
          maxLength={MAX_QUESTION_LENGTH}
          aria-label="AI chat input"
        />
        <SendButton
          $hasValue={hasValue && !isOverLimit}
          onClick={handleSubmit}
          disabled={disabled || isOverLimit}
          aria-label="Send"
          type="button"
        >
          <Icon icon={ICON_SEND_V2} size={20} />
          <SendTooltip className="send-tooltip">Send</SendTooltip>
        </SendButton>
      </InputBox>
      {showCounter && (
        <CharCounter $over={isOverLimit}>
          {value.length.toLocaleString()} / {MAX_QUESTION_LENGTH.toLocaleString()}
        </CharCounter>
      )}
    </Wrapper>
  );
};

export default AiChatInput;
